import { PDFDocument } from "pdf-lib"

import type { PdfBoxName, PdfBoxSizeInches } from "./pdf"
import { getPdfBytes, hasPdfBox, pointsToInches } from "./pdf"

export type PdfUploadValidationReason =
  | "TRIM_BOX_MISMATCH"
  | "TRIM_ART_BOX_MISMATCH"
  | "BLEED_BOX_MISMATCH"
  | "BOX_MISSING"
  | "PARSE_FAILED"

export type CheckPdfUploadLabelBoxesInput = {
  source: string | File[] | Uint8Array
  expectedTrimBoxSize: PdfBoxSizeInches
  expectedBleedBoxSize: PdfBoxSizeInches
}

export type CheckPdfUploadLabelBoxesResult =
  | {
      status: true
    }
  | {
      status: false
      reason: PdfUploadValidationReason
      message: string
      missingBoxes?: PdfBoxName[]
      failedPageNumber?: number
    }

const mapPdfBoxSizeToInches = (box: {
  width: number
  height: number
}): PdfBoxSizeInches => ({
  width: pointsToInches(box.width),
  height: pointsToInches(box.height),
})

const SIZE_MATCH_EPSILON_INCHES = 0.01

const isSizeMatch = ({
  expected,
  actual,
}: {
  expected: PdfBoxSizeInches
  actual: PdfBoxSizeInches
}): boolean =>
  Math.abs(expected.width - actual.width) <= SIZE_MATCH_EPSILON_INCHES &&
  Math.abs(expected.height - actual.height) <= SIZE_MATCH_EPSILON_INCHES

const formatInches = (value: number): string => value.toFixed(2)

const formatSizeHint = (
  expected: PdfBoxSizeInches,
  actual: PdfBoxSizeInches,
): string =>
  ` Required ${formatInches(expected.height)}"(H) x ${formatInches(
    expected.width,
  )}"(W), received ${formatInches(actual.height)}"(H) x ${formatInches(actual.width)}"(W).`

export const checkPdfUploadLabelBoxes = async ({
  source,
  expectedTrimBoxSize,
  expectedBleedBoxSize,
}: CheckPdfUploadLabelBoxesInput): Promise<CheckPdfUploadLabelBoxesResult> => {
  try {
    const bytes = await getPdfBytes(source)
    const pdfDoc = await PDFDocument.load(bytes)
    for (
      let pageIndex = 0;
      pageIndex < pdfDoc.getPageCount();
      pageIndex += 1
    ) {
      const page = pdfDoc.getPage(pageIndex)
      const pageNumber = pageIndex + 1
      const missingBoxes: PdfBoxName[] = []

      if (!hasPdfBox(page, "TrimBox")) {
        missingBoxes.push("TrimBox")
      }
      if (!hasPdfBox(page, "BleedBox")) {
        missingBoxes.push("BleedBox")
      }
      if (!hasPdfBox(page, "ArtBox")) {
        missingBoxes.push("ArtBox")
      }

      if (missingBoxes.length) {
        return {
          status: false,
          reason: "BOX_MISSING",
          message: `PDF page ${pageNumber} must include ${missingBoxes.join(" and ")} for label validation.`,
          missingBoxes,
          failedPageNumber: pageNumber,
        }
      }

      const trimBox = page.getTrimBox()
      const bleedBox = page.getBleedBox()
      const artBox = page.getArtBox()

      const actualTrimBoxSize: PdfBoxSizeInches = mapPdfBoxSizeToInches(trimBox)
      const actualBleedBoxSize: PdfBoxSizeInches = mapPdfBoxSizeToInches(bleedBox)
      const actualArtBoxSize: PdfBoxSizeInches = mapPdfBoxSizeToInches(artBox)

      if (
        !isSizeMatch({
          expected: expectedTrimBoxSize,
          actual: actualTrimBoxSize,
        })
      ) {
        return {
          status: false,
          reason: "TRIM_BOX_MISMATCH",
          message: `TrimBox size does not match required trim dimensions on page ${pageNumber}.${formatSizeHint(
            expectedTrimBoxSize,
            actualTrimBoxSize,
          )}`,
          failedPageNumber: pageNumber,
        }
      }

      if (
        !isSizeMatch({
          expected: expectedBleedBoxSize,
          actual: actualBleedBoxSize,
        })
      ) {
        return {
          status: false,
          reason: "BLEED_BOX_MISMATCH",
          message: `BleedBox size does not match required trim + bleed dimensions on page ${pageNumber}.${formatSizeHint(
            expectedBleedBoxSize,
            actualBleedBoxSize,
          )}`,
          failedPageNumber: pageNumber,
        }
      }

      if (
        !isSizeMatch({
          expected: expectedTrimBoxSize,
          actual: actualArtBoxSize,
        })
      ) {
        return {
          status: false,
          reason: "TRIM_ART_BOX_MISMATCH",
          message: `ArtBox must match TrimBox on page ${pageNumber}.${formatSizeHint(
            actualTrimBoxSize,
            actualArtBoxSize,
          )}`,
          failedPageNumber: pageNumber,
        }
      }
    }

    return { status: true }
  } catch {
    return {
      status: false,
      reason: "PARSE_FAILED",
      message: "Unable to parse PDF file",
    }
  }
}
